/**
 * Labstep
 *
 * @module components/Organization/Analytics/Chart
 * @desc Chart component for analytics
 */

import { AgCharts } from 'ag-charts-react';
import type { AgChartOptions } from 'ag-grid-community';
import type { OrganizationAnalyticsChartProps } from 'labstep-web/components/Organization/Analytics/Chart/types';
import { useGetOrganizationMetricQuery } from 'labstep-web/rtk/services/organization-metric';
import React from 'react';

export const OrganizationAnalyticsChart: React.FC<
  OrganizationAnalyticsChartProps
> = ({ title, interval, metric }) => {
  const { data, isLoading, error } = useGetOrganizationMetricQuery({
    interval,
    metric,
  });

  const chartData = React.useMemo(() => {
    if (!data) {
      return [];
    }

    return data
      .map((item) => ({
        date: new Date(item.created_at),
        value: item.metric,
        formattedDate: new Date(item.created_at).toLocaleDateString(
          'en-US',
          {
            year: 'numeric',
            month: 'short',
            day: interval === 'monthly' ? undefined : 'numeric',
          },
        ),
      }))
      .sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [data, interval]);

  const chartOptions: AgChartOptions = React.useMemo(() => {
    return {
      data: chartData,
      subtitle: {
        text: title,
      },
      series: [
        {
          type: 'line',
          xKey: 'date',
          yKey: 'value',
          stroke: '#3366CC',
          strokeWidth: 3,
          marker: {
            enabled: true,
            fill: '#3366CC',
            stroke: '#ffffff',
            strokeWidth: 2,
            size: 8,
          },
          tooltip: {
            renderer: function ({ datum, xKey, yKey }) {
              return `
      <div style="padding: 8px 12px;">
        <div class="ag-chart-tooltip-title" style="margin-bottom: 4px; font-weight: bold;">${datum.formattedDate}</div>
        <div class="ag-chart-tooltip-content"><b>${datum[yKey]}</b></div>
      </div>
    `;
            },
          },
        },
      ],
      axes: [
        {
          type: 'time',
          position: 'bottom',
        },
        {
          type: 'number',
          position: 'left',
        },
      ],
    };
  }, [chartData, title, metric]);

  if (isLoading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div>Loading chart data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{ padding: '20px', textAlign: 'center', color: 'red' }}
      >
        <div>Error loading chart data</div>
      </div>
    );
  }

  if (!chartData.length) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div>{title}</div>
        <div>No data available</div>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', height: '400px' }}>
      <AgCharts options={chartOptions as any} />
    </div>
  );
};
